import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { CircleF, GoogleMap, MarkerF } from "@react-google-maps/api";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import classes from "../myteam.module.css";
import { BrudCrumbBackIcon, BrudCrumbNextIcon } from "../../../../components/Icons";
import {
  CIRCLE_OPTIONS,
  GF,
  clampRadius,
  fetchTeamBySlug,
  fitMapToCircle,
  formatRadius,
  getErrorMessage,
  modeLabel,
  placeLabel,
  removeTeamGeofence,
  saveTeamGeofence,
} from "./geofenceApi";
import {
  DetailRow,
  GeofenceMap,
  MemberChecklist,
  ModeToggle,
  RadiusControl,
  useGeofenceMapsLoader,
} from "./GeofenceParts";

const primaryButtonSx = {
  flex: 1,
  py: 1.5,
  borderRadius: "14px",
  textTransform: "none",
  fontSize: 16,
  fontWeight: 600,
  bgcolor: GF.primary,
  boxShadow: "none",
};
const secondaryButtonSx = {
  flex: 1,
  py: 1.5,
  borderRadius: "14px",
  textTransform: "none",
  fontSize: 16,
  fontWeight: 600,
  color: GF.foreground,
  borderColor: GF.border,
};

/** Editor dialog shell: title, help text, body, Cancel / Save. */
const EditorDialog = ({ open, title, subtitle, onClose, onSave, saving, saveLabel = "Save", maxWidth = "xs", children }) => (
  <Dialog
    open={open}
    onClose={() => !saving && onClose()}
    fullWidth
    maxWidth={maxWidth}
    PaperProps={{ sx: { borderRadius: "24px", p: 1 } }}
  >
    <DialogTitle sx={{ fontSize: 20, fontWeight: 700, color: GF.foreground, pb: 0.5 }}>{title}</DialogTitle>
    <DialogContent>
      {subtitle && <Typography sx={{ fontSize: 14, color: GF.muted, mb: 2.5 }}>{subtitle}</Typography>}
      {children}
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2.5, gap: 1.5 }}>
      <Button variant="outlined" onClick={onClose} disabled={saving} sx={secondaryButtonSx}>
        Cancel
      </Button>
      <Button variant="contained" onClick={onSave} disabled={saving} sx={{ ...primaryButtonSx, ml: "0 !important" }}>
        {saving ? <CircularProgress size={22} color="inherit" /> : saveLabel}
      </Button>
    </DialogActions>
  </Dialog>
);

/** Saved geofence: each field is editable, plus Remove Geofence. */
const GeofenceView = () => {
  const navigate = useNavigate();
  const { name: slug } = useParams();
  const { isLoaded } = useGeofenceMapsLoader();

  const [team, setTeam] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [editor, setEditor] = useState(null); // 'location' | 'radius' | 'mode' | 'members'
  const [draftPlace, setDraftPlace] = useState(null);
  const [draftRadius, setDraftRadius] = useState(0);
  const [draftMode, setDraftMode] = useState("time_in_out");
  const [draftSelected, setDraftSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(() => {
    setLoadError(false);
    fetchTeamBySlug(slug)
      .then(setTeam)
      .catch((error) => {
        setLoadError(true);
        toast.error(getErrorMessage(error, "Could not load geofence"));
      });
  }, [slug]);

  useEffect(load, [load]);

  const geofence = team?.geofence?.lat != null ? team.geofence : null;
  const members = useMemo(
    () => (team?.team_members || []).filter((m) => m && m._id && m.user_name),
    [team]
  );
  const excluded = useMemo(
    () => new Set((geofence?.excluded_member_ids || []).map(String)),
    [geofence]
  );
  const appliesCount = members.filter((m) => !excluded.has(m._id)).length;
  const center = geofence ? { lat: geofence.lat, lng: geofence.lng } : null;

  const openEditor = (next) => {
    if (next === "location") {
      setDraftPlace({ name: geofence.name, address: geofence.address, lat: geofence.lat, lng: geofence.lng });
    }
    if (next === "radius") setDraftRadius(geofence.radius_meters);
    if (next === "mode") setDraftMode(geofence.mode || "time_in_out");
    if (next === "members") {
      setDraftSelected(new Set(members.filter((m) => !excluded.has(m._id)).map((m) => m._id)));
    }
    setEditor(next);
  };

  const saveEditor = async () => {
    let body;
    if (editor === "location") {
      if (!draftPlace) return;
      body = { lat: draftPlace.lat, lng: draftPlace.lng, name: draftPlace.name, address: draftPlace.address };
    } else if (editor === "radius") body = { radius_meters: clampRadius(draftRadius) };
    else if (editor === "mode") body = { mode: draftMode };
    else if (editor === "members") {
      body = { excluded_member_ids: members.filter((m) => !draftSelected.has(m._id)).map((m) => m._id) };
    }
    setSaving(true);
    try {
      const saved = await saveTeamGeofence(team._id, body);
      setTeam((prev) => ({ ...prev, geofence: saved }));
      setEditor(null);
      toast.success("Geofence saved");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not save geofence"));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await removeTeamGeofence(team._id);
      toast.success("Geofence removed");
      navigate(`/my-team/${slug}`);
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not remove geofence"));
      setRemoving(false);
    }
  };

  const renderBody = () => {
    if (!team) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, gap: 2 }}>
          {loadError ? (
            <Button onClick={load} sx={{ textTransform: "none" }}>
              Retry
            </Button>
          ) : (
            <CircularProgress />
          )}
        </Box>
      );
    }
    if (!geofence) {
      return (
        <Card variant="outlined" sx={{ borderRadius: "14px", p: 4, textAlign: "center" }}>
          <Typography sx={{ color: GF.muted, mb: 2 }}>No geofence is set for this team.</Typography>
          <Button
            variant="contained"
            onClick={() => navigate(`/my-team/${slug}/geofence/setup`)}
            sx={{ ...primaryButtonSx, flex: "none", px: 4 }}
          >
            + Add Geofence
          </Button>
        </Card>
      );
    }

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ borderRadius: "14px", overflow: "hidden", height: { xs: 280, md: 460 } }}>
            {isLoaded ? (
              <GoogleMap
                key={`${geofence.lat},${geofence.lng},${geofence.radius_meters}`}
                mapContainerStyle={{ width: "100%", height: "100%" }}
                center={center}
                zoom={15}
                onLoad={(map) => fitMapToCircle(map, center, geofence.radius_meters)}
                options={{ disableDefaultUI: true, zoomControl: true, clickableIcons: false }}
              >
                <CircleF center={center} radius={geofence.radius_meters} options={CIRCLE_OPTIONS} />
                <MarkerF position={center} />
              </GoogleMap>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                <CircularProgress />
              </Box>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <DetailRow
              icon={FmdGoodOutlinedIcon}
              label="Location"
              value={placeLabel(geofence)}
              onClick={() => openEditor("location")}
            />
            <DetailRow
              icon={MapOutlinedIcon}
              label="Radius"
              value={formatRadius(geofence.radius_meters)}
              onClick={() => openEditor("radius")}
            />
            <DetailRow
              icon={AccessTimeIcon}
              label="Enabled for"
              value={modeLabel(geofence.mode)}
              onClick={() => openEditor("mode")}
            />
            <DetailRow
              icon={PeopleAltOutlinedIcon}
              label="Applies to"
              value={`${appliesCount} of ${members.length} members`}
              onClick={() => openEditor("members")}
            />

            <ButtonBase
              onClick={() => setConfirmRemove(true)}
              sx={{
                mt: 2,
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: "14px",
                border: `1px solid ${GF.border}`,
                borderRadius: "14px",
                textAlign: "left",
                "&:hover": { bgcolor: "#FFF8F8" },
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "8px",
                  bgcolor: GF.destructiveTint,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <CloseIcon sx={{ fontSize: 20, color: GF.destructive }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 17, fontWeight: 600, color: GF.destructive }}>
                  Remove Geofence
                </Typography>
                <Typography sx={{ fontSize: 13, color: GF.muted }}>
                  Players will no longer be limited to this zone
                </Typography>
              </Box>
            </ButtonBase>
          </Box>
        </Grid>
      </Grid>
    );
  };

  return (
    <div style={{ margin: "0 auto 0 15px" }}>
      <Box sx={{ mb: 3 }} className={classes.brudCrumb}>
        <span onClick={() => navigate(`/my-team/${slug}`)}>
          <BrudCrumbBackIcon />
        </span>
        <span onClick={() => navigate("/my-team")}>My Team</span>
        <BrudCrumbNextIcon />
        <span onClick={() => navigate(`/my-team/${slug}`)}>{team?.team_name || ""}</span>
        <BrudCrumbNextIcon />
        <span style={{ color: "#6A6E8A" }}>Geo Fencing</span>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Typography sx={{ fontSize: 22, fontWeight: 600, color: GF.foreground }}>Geo Fencing</Typography>
        {geofence && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              px: 1.5,
              py: 0.75,
              borderRadius: 999,
              bgcolor: GF.successTint,
            }}
          >
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: GF.success }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: GF.success }}>Geofence Active</Typography>
          </Box>
        )}
      </Box>

      {renderBody()}

      <EditorDialog
        open={editor === "location"}
        title="Edit Location"
        onClose={() => setEditor(null)}
        onSave={saveEditor}
        saving={saving}
        saveLabel="Confirm Location"
        maxWidth="md"
      >
        <Box sx={{ borderRadius: "14px", overflow: "hidden", border: `1px solid ${GF.border}` }}>
          <GeofenceMap
            isLoaded={isLoaded}
            place={draftPlace}
            radius={geofence?.radius_meters || 0}
            onPlaceChange={setDraftPlace}
            height={420}
          />
        </Box>
        {draftPlace && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: GF.muted }}>Selected Location:</Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: GF.foreground }}>
              {placeLabel(draftPlace) || "Selected location"}
            </Typography>
            {draftPlace.address && (
              <Typography sx={{ fontSize: 14, color: GF.muted }}>{draftPlace.address}</Typography>
            )}
          </Box>
        )}
      </EditorDialog>

      <EditorDialog
        open={editor === "radius"}
        title="Change Radius"
        subtitle="Players can time in or out only within this distance of the location."
        onClose={() => setEditor(null)}
        onSave={saveEditor}
        saving={saving}
      >
        <RadiusControl value={draftRadius} onChange={setDraftRadius} large />
      </EditorDialog>

      <EditorDialog
        open={editor === "mode"}
        title="Enabled For"
        subtitle="Choose which actions players can perform inside this geofence."
        onClose={() => setEditor(null)}
        onSave={saveEditor}
        saving={saving}
        maxWidth="sm"
      >
        <ModeToggle value={draftMode} onChange={setDraftMode} />
      </EditorDialog>

      <EditorDialog
        open={editor === "members"}
        title="Select Members"
        subtitle="Unselected members can time in and out from anywhere."
        onClose={() => setEditor(null)}
        onSave={saveEditor}
        saving={saving}
        maxWidth="sm"
      >
        <MemberChecklist
          members={members}
          selectedIds={draftSelected}
          onChange={setDraftSelected}
          maxHeight={360}
        />
      </EditorDialog>

      <Dialog
        open={confirmRemove}
        onClose={() => !removing && setConfirmRemove(false)}
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Remove geofence?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Players in this team will be able to time in and out from anywhere.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmRemove(false)} disabled={removing} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRemove}
            disabled={removing}
            sx={{ textTransform: "none", bgcolor: GF.destructive, "&:hover": { bgcolor: "#d63737" } }}
          >
            {removing ? <CircularProgress size={20} color="inherit" /> : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default GeofenceView;
